import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { factorService } from '../../services/api';
import CampText from '../CampText';

const ExtremeValuesStep = ({ project, factors, drivers, onDataChange }) => {
  const { t } = useTranslation();
  const [extremeValues, setExtremeValues] = useState({});
  const [showHelpModal, setShowHelpModal] = useState(false);

  const qualifiedDrivers = factors.filter(f => f.is_driver);

  const getTranslatedCategory = (category) => {
    const categoryMap = {
      'political': t('factors.pestel.political'),
      'economic': t('factors.pestel.economic'),
      'social': t('factors.pestel.social'),
      'technological': t('factors.pestel.technological'),
      'environmental': t('factors.pestel.environmental'),
      'legal': t('factors.pestel.legal'),
      'strengths': t('factors.swot.strengths'),
      'weaknesses': t('factors.swot.weaknesses'),
      'opportunities': t('factors.swot.opportunities'),
      'threats': t('factors.swot.threats'),
      'identify': t('factors.cyber.identify'),
      'protect': t('factors.cyber.protect'),
      'detect': t('factors.cyber.detect'),
      'respond': t('factors.cyber.respond'),
      'recover': t('factors.cyber.recover')
    };
    
    return categoryMap[category] || category;
  };

  /**
   * Desa un dels dos extrems.
   *
   * L'esquema del servidor demana **els dos alhora** (`DriverExtremeValues` té
   * `extreme_value_low` i `extreme_value_high` obligatoris), o sigui que
   * s'envien tots dos i el que encara no s'ha escrit va com a cadena buida.
   *
   * Abans això anava amb un botó «Desa» per fila, un «Edita» per tornar-hi i
   * una etiqueta «(Actual)» sota el valor ja desat. Els tres sobraven: el valor
   * desat ja es veu perquè hi és, i «(Actual)» no vol dir res per a qui ho
   * llegeix —ni surt enlloc del mètode.
   */
  const desaExtrem = async (factorId, camp, valor) => {
    const driver = drivers.find(d => d.causal_factor_id === factorId);
    const actuals = {
      extreme_value_low: driver?.extreme_value_low || '',
      extreme_value_high: driver?.extreme_value_high || '',
      ...(extremeValues[factorId] || {})
    };
    const nous = { ...actuals, [camp]: valor };
    if (nous.extreme_value_low === actuals.extreme_value_low
        && nous.extreme_value_high === actuals.extreme_value_high) return;

    setExtremeValues(prev => ({ ...prev, [factorId]: nous }));
    try {
      await factorService.updateDriverExtremes(factorId, nous);
      onDataChange();
    } catch (error) {
      console.error('Error updating extreme values:', error);
      toast.error(t('factors.extremeValues.updateError'));
    }
  };

  const getDriverForFactor = (factorId) => {
    return drivers.find(d => d.causal_factor_id === factorId);
  };

  const completedDrivers = drivers.filter(d => d.extreme_value_low && d.extreme_value_high);

  return (
    <div className="space-y-6">
      {qualifiedDrivers.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            {t('factors.extremeValues.noQualifiedDrivers')}
          </h3>
          <p className="text-neutral-600">
            {t('factors.driverTest.addFactorsFirst')}
          </p>
        </div>
      ) : (
        <>
          {/* Compact Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-neutral-900">{t('factors.extremeValues.title')}</h3>
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="w-5 h-5 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-neutral-500 hover:text-neutral-700 transition-colors"
                  title={t('factors.extremeValues.showHelp')}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-neutral-600">
                  {t('factors.extremeValues.driversStatus', { completed: completedDrivers.length, total: qualifiedDrivers.length })}
                </span>
                {project.time_horizon && (
                  <div className="flex items-center gap-1 text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {project.time_horizon}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Drivers Table */}
          <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/4">
                      {t('factors.driverTest.factor')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/3">
                      <span className="mr-2 font-mono text-neutral-400">{t('factors.extremeValues.shortFirst')}</span>
                      {t('factors.extremeValues.low')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/3">
                      <span className="mr-2 font-mono text-neutral-400">{t('factors.extremeValues.shortSecond')}</span>
                      {t('factors.extremeValues.high')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {qualifiedDrivers.map(factor => {
                    const driver = getDriverForFactor(factor.id);
                    
                    return (
                      <tr key={factor.id} className="hover:bg-neutral-50">
                        {/* Factor Info */}
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-neutral-200 rounded-full flex items-center justify-center text-sm font-medium text-neutral-600 mr-3">
                              {factor.name.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-neutral-900">{factor.name}</div>
                              <div className="text-sm text-neutral-500">{getTranslatedCategory(factor.category)}</div>
                            </div>
                          </div>
                        </td>

                        {/* Un extrem */}
                        <td className="px-6 py-4">
                          <CampText
                            value={driver?.extreme_value_low}
                            onSave={(v) => desaExtrem(factor.id, 'extreme_value_low', v)}
                            placeholder={t('factors.extremeValues.lowPlaceholder')}
                            multiline
                            rows={2}
                            inputClassName="text-sm"
                          />
                        </td>

                        {/* L'altre */}
                        <td className="px-6 py-4">
                          <CampText
                            value={driver?.extreme_value_high}
                            onSave={(v) => desaExtrem(factor.id, 'extreme_value_high', v)}
                            placeholder={t('factors.extremeValues.highPlaceholder')}
                            multiline
                            rows={2}
                            inputClassName="text-sm"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Help Modal */}
          {showHelpModal && (
            <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl">
                <div className="p-6 border-b border-neutral-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-neutral-900">
                      {t('factors.extremeValues.helpTitle')}
                    </h3>
                    <button
                      onClick={() => setShowHelpModal(false)}
                      className="text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="prose prose-sm max-w-none text-neutral-700">
                    <p className="mb-4">
                      {t('factors.extremeValues.instructionsText', { timeHorizon: project.time_horizon || t('common.notSpecified') })}
                    </p>
                    
                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-900 mb-3">{t('factors.extremeValues.guidelines')}:</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm text-neutral-600">{t('factors.extremeValues.guideline1')}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm text-neutral-600">{t('factors.extremeValues.guideline2')}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-sm text-neutral-600">{t('factors.extremeValues.guideline3')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExtremeValuesStep;